import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import api, { API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Plus, Trash2, Edit3, Package, DollarSign, ShoppingBag, BarChart3, Tag, X, Gavel, Zap, Send } from "lucide-react";

import { toast } from "sonner";
import { formatIDR, formatNumber } from "../lib/format";
import OrderChatPanel from "../components/OrderChatPanel";
import SellerProfileCard from "../components/SellerProfileCard";
import { useWallet } from "../context/WalletContext";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
} from "recharts";

const emptyProduct = { title: "", description: "", price: "", modal: "", category: "mobile-legends", type: "akun", stock: 100, image_url: "" };
const emptyCoupon = { code: "", discount_type: "percentage", value: 10, expires_at: "", max_uses: "", min_order_amount: 0 };

const BACKEND = API.replace(/\/api$/, "");
const resolveImg = (url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${BACKEND}${url}`;
};

export default function SellerDashboard() {
  const { user, loading } = useAuth();
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [tab, setTab] = useState("products");
  const [chatModal, setChatModal] = useState(null);

  // Product form
  const [form, setForm] = useState(emptyProduct);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  // Coupon form

  const [couponForm, setCouponForm] = useState(emptyCoupon);
  const [showCouponForm, setShowCouponForm] = useState(false);

  // Request Produk modal
  const [showRequestModal, setShowRequestModal] = useState(false);

  // Modal "Kelola Kode" (Voucher / Akun). Menyimpan produk yang sedang dikelola
  // dan tipenya ("voucher" | "akun") supaya judul & placeholder pas.
  const [manageCodes, setManageCodes] = useState(null); // { product, kind }


  const refresh = async () => {
    if (!user) return;
    const [p, o, c, t] = await Promise.all([
      api.get(`/products?seller_id=${user.id}`),
      api.get("/orders/seller"),
      api.get("/categories"),
      api.get("/product-types"),
    ]);
    setProducts(p.data);
    setOrders(o.data);
    setCategories(c.data);
    setProductTypes(t.data);
  };

  const refreshCoupons = async () => {
    const { data } = await api.get("/coupons");
    setCoupons(data);
  };

  const refreshAnalytics = async () => {
    const { data } = await api.get("/analytics/seller?days=30");
    setAnalytics(data);
  };

  useEffect(() => {
    if (user && user.role === "seller") refresh();
    // eslint-disable-next-line
  }, [user]);

  useEffect(() => {
    if (user && user.role === "seller") {
      if (tab === "coupons") refreshCoupons();
      if (tab === "analytics") refreshAnalytics();
    }
    // eslint-disable-next-line
  }, [tab, user]);

  if (loading) return <div className="text-white/50 text-center py-32">Loading...</div>;
  if (!user || user === false) return <Navigate to="/login" replace />;
  if (user.role !== "seller") return <Navigate to="/become-seller" replace />;

  const submitProduct = async (e) => {
    e.preventDefault();
    // Parse kode voucher multi-baris (satu kode per baris) dari Instant Delivery Fields.
    // Field "Kode Voucher" diperlakukan sebagai daftar kode yang akan masuk ke pool `codes`
    // produk (maks 1000 kode). Field instant_delivery_data lain tetap disimpan apa adanya.
    const instantData = { ...(form.instant_delivery_data || {}) };
    const voucherFieldKey = Object.keys(instantData).find(
      (k) => (k || "").toLowerCase().trim() === "kode voucher"
    );

    let codes = null;
    if (voucherFieldKey) {
      const raw = (instantData[voucherFieldKey] || "").toString();
      const parsed = raw
        .split(/\r?\n|,/g) // dukung newline ATAU koma sebagai pemisah
        .map((c) => c.trim())
        .filter((c) => c.length > 0);
      // dedupe (case-insensitive untuk mencegah duplikat)
      const seen = new Set();
      const unique = [];
      for (const c of parsed) {
        const key = c.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        unique.push(c);
      }
      if (unique.length > 1000) {
        toast.error("Maksimal 1000 kode voucher. Kurangi jumlah kode.");
        return;
      }
      if (unique.length > 0) {
        codes = unique;
        // Hapus field Kode Voucher dari instant_delivery_data karena sudah direpresentasikan
        // oleh pool `codes`. Seller tidak perlu lihat field ini dua kali di database.
        delete instantData[voucherFieldKey];
      } else {
        // User sengaja kosongkan -> jangan kirim field ini di payload update
        delete instantData[voucherFieldKey];
      }
    }

    // Modal adalah field privat seller (harga pokok). Selalu bawa nilainya
    // walaupun kosong (dikirim sebagai null) supaya:
    //   - Backend bisa membedakan "tidak diubah" vs "diubah ke 0".
    //   - Untuk CREATE: tidak ada modal = backend menyimpan None (tidak ada).
    //   - Untuk EDIT: kirim nilai eksplisit (string kosong → null = hapus modal).
    // Konversi: string kosong -> null, string dengan angka -> float. Jangan
    // kirim undefined supaya tidak di-drop oleh serializer Pydantic.
    const modalRaw = form.modal;
    const modalNumeric =
      modalRaw === "" || modalRaw === null || modalRaw === undefined
        ? null
        : parseFloat(modalRaw);
    const modalValue = modalNumeric === null || Number.isNaN(modalNumeric) ? null : modalNumeric;
    if (modalValue !== null && modalValue < 0) {
      toast.error("Modal tidak boleh negatif");
      return;
    }

    const payload = {
      ...form,
      price: parseFloat(form.price),
      stock: parseInt(form.stock) || 0,
      modal: modalValue,
    };
    // Selalu kirim instant_delivery_data (kalau ada isinya) supaya field lain tidak hilang.
    if (Object.keys(instantData).length > 0) {
      payload.instant_delivery_data = instantData;
    } else if ("instant_delivery_data" in form) {
      payload.instant_delivery_data = {};
    }
    // Untuk create, sertakan `codes` agar backend langsung membuat pool kode.
    if (!editing && codes) {
      payload.codes = codes;
    }

    // === FIX BUG EDIT PRODUK (ubidstore.com) ===
    // Endpoint PUT /products/{id} HANYA menerima field: description, price,
    // stock (untuk produk non-akun/voucher), dan modal. Field lain seperti
    // title, category, type, image_url, instant_delivery_data akan ditolak
    // backend dengan HTTP 400: "Field yang boleh diedit setelah rilis...".
    // Sebelumnya frontend mengirim payload form LENGKAP -> edit selalu gagal.
    // Solusi: saat mode EDIT, kirim hanya field yang diizinkan.
    if (editing) {
      const editable = {
        description: payload.description,
        price: payload.price,
        modal: payload.modal,
      };
      // CATATAN: Untuk produk INSTANT DELIVERY (tipe "voucher" / "akun"),
      // stok DITENTUKAN OTOMATIS oleh jumlah kode yang belum terjual di pool
      // `codes`. Backend AKAN MENOLAK (HTTP 400) bila field `stock` dikirim
      // untuk produk jenis ini ("Edit stok lewat tombol Edit Kode, bukan
      // lewat field stok."). Karena itu, kirim `stock` HANYA untuk produk
      // non-instant. Stok voucher/akun diubah lewat "Kelola Voucher/Akun".
      const instantTypes = ["voucher", "akun"];
      const isInstant = instantTypes.includes((form.type || "").toLowerCase());
      if (!isInstant) {
        editable.stock = payload.stock;
      }
      Object.keys(payload).forEach((k) => { delete payload[k]; });
      Object.assign(payload, editable);
    }
    // === END FIX ===


    try {
      let productId = editing;
      if (editing) {
        await api.put(`/products/${editing}`, payload);
        toast.success("Product updated");
        // Untuk edit, sinkronkan pool kode lewat endpoint khusus (menjaga kode yang sudah sold)
        if (codes) {
          try {
            await api.put(`/products/${editing}/codes`, { codes });
            toast.success(`${codes.length} kode voucher diperbarui`);
          } catch (err) {
            toast.error(err.response?.data?.detail || "Gagal update kode voucher");
            return;
          }
        }
      } else {
        const { data } = await api.post("/products", payload);
        productId = data?.id;
        if (codes && codes.length > 0) {
          toast.success(`Product created dengan ${codes.length} kode voucher`);
        } else {
          toast.success("Product created");
        }
      }
      setForm(emptyProduct);
      setEditing(null);
      setShowForm(false);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const removeProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Deleted");
    refresh();
  };

  const startEdit = (p) => {
    setEditing(p.id);
    setForm({
      title: p.title,
      description: p.description,
      price: p.price,
      // Modal adalah field privat seller. Selalu prefilled baik ada
      // maupun null supaya seller bisa lihat / hapus nilainya. Backend
      // hanya akan menyimpan perubahan nilai modal bila field dikirim.
      modal: p.modal != null && p.modal !== "" ? p.modal : "",
      category: p.category,
      type: p.type,
      stock: p.stock,
      image_url: p.image_url || "",
    });
    setShowForm(true);
  };

  const submitCoupon = async (e) => {

    e.preventDefault();
    const payload = {
      code: couponForm.code.trim().toUpperCase(),
      discount_type: couponForm.discount_type,
      value: parseFloat(couponForm.value),
      expires_at: couponForm.expires_at || null,
      max_uses: couponForm.max_uses ? parseInt(couponForm.max_uses) : null,
      min_order_amount: parseFloat(couponForm.min_order_amount) || 0,
    };
    try {
      await api.post("/coupons", payload);
      toast.success("Coupon created");
      setCouponForm(emptyCoupon);
      setShowCouponForm(false);
      refreshCoupons();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const removeCoupon = async (id) => {
    if (!window.confirm("Delete this coupon?")) return;
    await api.delete(`/coupons/${id}`);
    toast.success("Deleted");
    refreshCoupons();
  };

  const totalRevenue = orders.reduce((s, o) => s + (o.seller_total || 0), 0);

  return (
    <div data-testid="seller-dashboard-page" className="max-w-7xl mx-auto px-4 md:px-8 py-12">
      <div className="flex items-end justify-between mb-8">
        <div>
          <span className="tag tag-accent">// SELLER COMMAND</span>
          <h1 className="font-display font-black text-3xl md:text-5xl tracking-tighter text-white mt-3">
            {user.shop_name || "My Shop"}
          </h1>
          <p className="text-white/55 mt-2">{user.email}</p>
        </div>
      </div>

      {/* Profil Seller Card */}
      <SellerProfileCard user={user} onUpdate={refresh} />

      {/* Stats */}
      <div className="grid md:grid-cols-3 gap-4 mb-8">
        {[
          { icon: Package, label: "Products", value: products.length },
          { icon: ShoppingBag, label: "Paid Orders", value: orders.length },
          { icon: DollarSign, label: "Revenue", value: formatIDR(totalRevenue) },
        ].map((s) => (
          <div key={s.label} className="border border-white/10 p-6">
            <s.icon className="w-5 h-5 text-[#00F0FF] mb-3" />
            <div className="font-display font-black text-3xl text-white">{s.value}</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="border-b border-white/10 mb-6 flex gap-1 flex-wrap">
        {[
          { id: "products", label: "Products" },
          { id: "orders", label: "Orders" },
          { id: "auctions", label: "Auctions" },
          { id: "withdraw", label: "Penarikan" },
          { id: "coupons", label: "Coupons" },
          { id: "analytics", label: "Analytics" },
        ].map((t) => (
          <button
            key={t.id}
            data-testid={`tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-4 py-3 text-xs uppercase tracking-widest font-bold ${
              tab === t.id ? "text-[#00F0FF] border-b-2 border-[#00F0FF]" : "text-white/50 hover:text-white/80"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "products" && (
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-6">
            <button
              data-testid="add-product-btn"
              onClick={() => {
                setForm(emptyProduct);
                setEditing(null);
                setShowForm((v) => !v);
              }}
              className="btn-primary inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              {showForm ? "Close form" : "Add Product"}
            </button>
            <button
              data-testid="request-product-btn"
              onClick={() => setShowRequestModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 border border-[#FFD700]/50 text-[#FFD700] hover:bg-[#FFD700]/10 text-xs uppercase tracking-widest font-bold"
            >
              <Send className="w-4 h-4" />
              Request Produk
            </button>

            {/* === Kelola Voucher / Kelola Akun ===
                Dua tombol untuk mengedit stok kode (voucher) atau detail akun
                KAPAN SAJA setelah produk di-post. Menyelesaikan masalah: data
                instant delivery yang tidak tersimpan / tidak bisa diedit.
                - "Kelola Voucher" => buka picker produk bertipe voucher.
                - "Kelola Akun"    => buka picker produk bertipe akun.
                Jika hanya ada 1 produk pada tipe tsb, langsung buka editornya. */}
            <button
              data-testid="manage-voucher-btn"
              onClick={() => {
                const list = products.filter((p) => (p.type || "").toLowerCase() === "voucher");
                if (list.length === 0) { toast.error("Belum ada produk bertipe Voucher."); return; }
                setManageCodes({ product: list.length === 1 ? list[0] : null, kind: "voucher" });
              }}
              className="inline-flex items-center gap-2 px-4 py-2 border border-[#00FF66]/50 text-[#00FF66] hover:bg-[#00FF66]/10 text-xs uppercase tracking-widest font-bold"
            >
              <Tag className="w-4 h-4" />
              Kelola Voucher
            </button>
            <button
              data-testid="manage-akun-btn"
              onClick={() => {
                const list = products.filter((p) => (p.type || "").toLowerCase() === "akun");
                if (list.length === 0) { toast.error("Belum ada produk bertipe Akun."); return; }
                setManageCodes({ product: list.length === 1 ? list[0] : null, kind: "akun" });
              }}
              className="inline-flex items-center gap-2 px-4 py-2 border border-[#00F0FF]/50 text-[#00F0FF] hover:bg-[#00F0FF]/10 text-xs uppercase tracking-widest font-bold"
            >
              <Package className="w-4 h-4" />
              Kelola Akun
            </button>
          </div>


          {showForm && (
            <form onSubmit={submitProduct} className="border border-white/10 p-6 mb-6 grid md:grid-cols-2 gap-4" data-testid="product-form">
              <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-[0.2em] text-white/60">Title</label>
                <input data-testid="product-title-input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full bg-black border border-white/15 focus:border-[#00F0FF] p-3 text-sm mt-2" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-[0.2em] text-white/60">Description</label>
                <textarea data-testid="product-desc-input" required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="w-full bg-black border border-white/15 focus:border-[#00F0FF] p-3 text-sm mt-2" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-white/60">Category</label>
                <select data-testid="product-category-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full bg-black border border-white/15 focus:border-[#00F0FF] p-3 text-sm mt-2">
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
               <div>
                 <label className="text-xs uppercase tracking-[0.2em] text-white/60">Tipe Produk</label>
                 <select data-testid="product-type-input" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full bg-black border border-white/15 focus:border-[#00F0FF] p-3 text-sm mt-2">
                   {productTypes.length === 0 && <option value="">-- Admin belum tentukan tipe --</option>}
                   {productTypes.map((t) => (
                     <option key={t.id} value={t.id}>{t.label}</option>
                   ))}
                 </select>
                 <p className="text-[10px] text-white/40 mt-1">Tipe diatur oleh admin. Kalau kurang, ajukan via "Request Produk".</p>
               </div>

               {/* Instant Delivery Fields - Muncul jika tipe produk punya fields.
                   CATATAN PENTING: Field ini HANYA ditampilkan saat MEMBUAT produk
                   baru (mode create). Saat MENGEDIT produk (mode edit), field ini
                   DISEMBUNYIKAN sepenuhnya supaya:
                     - Seller bisa mengubah harga / deskripsi / stok / modal tanpa
                       terganggu / tanpa risiko menimpa pool kode voucher.
                     - Perubahan kode voucher/akun dilakukan KHUSUS lewat tombol
                       "Kelola Voucher" / "Kelola Akun" (endpoint /codes yang aman,
                       menjaga kode yang sudah terjual). */}
               {!editing && (() => {
                 const selectedType = productTypes.find((t) => t.id === form.type);
                 const fields = selectedType?.instant_delivery_fields || [];
                 if (fields.length === 0) return null;
                 return (

                   <div className="md:col-span-2 border border-[#00FF66]/30 bg-[#00FF66]/5 p-4">
                     <div className="flex items-center gap-2 mb-3">
                       <Zap className="w-4 h-4 text-[#00FF66]" />
                       <label className="text-xs uppercase tracking-[0.2em] text-[#00FF66] font-bold">Instant Delivery Fields</label>
                     </div>
                     <p className="text-[10px] text-white/60 mb-3">Isi field di bawah. Informasi ini akan diberikan ke buyer setelah pembayaran.</p>
                     <div className="space-y-3">
                       {fields.map((fieldName, idx) => {
                         const isVoucherField = (fieldName || "").toLowerCase().trim() === "kode voucher";
                         return (
                         <div key={idx}>
                           <div className="flex items-center justify-between">
                             <label className="text-[10px] uppercase tracking-widest text-white/70 capitalize">{fieldName}</label>
                             {isVoucherField && (
                               <span
                                 className="text-[10px] uppercase tracking-widest text-[#00FF66]"
                                 data-testid={`instant-delivery-${fieldName}-count`}
                               >
                                 {((form.instant_delivery_data?.[fieldName] || "").split(/\r?\n|,/).map((c) => c.trim()).filter((c) => c.length > 0).length) || 0} kode
                                 {" "}· maks 1000
                               </span>
                             )}
                           </div>
                           {isVoucherField ? (
                             <textarea
                               rows={5}
                               placeholder={`Satu kode per baris, misal:\nabc123\ndfkhg-xyz\nLAINNYA`}
                               value={form.instant_delivery_data?.[fieldName] || ""}
                               onChange={(e) => setForm({
                                 ...form,
                                 instant_delivery_data: {
                                   ...form.instant_delivery_data,
                                   [fieldName]: e.target.value
                                 }
                               })}
                               className="w-full bg-black border border-[#00FF66]/40 focus:border-[#00FF66] p-2 text-sm mt-1 font-mono"
                               data-testid={`instant-delivery-${fieldName}`}
                             />
                           ) : (
                             <input
                               type="text"
                               placeholder={`Masukkan ${fieldName}...`}
                               value={form.instant_delivery_data?.[fieldName] || ""}
                               onChange={(e) => setForm({
                                 ...form,
                                 instant_delivery_data: {
                                   ...form.instant_delivery_data,
                                   [fieldName]: e.target.value
                                 }
                               })}
                               className="w-full bg-black border border-[#00FF66]/40 focus:border-[#00FF66] p-2 text-sm mt-1"
                               data-testid={`instant-delivery-${fieldName}`}
                             />
                           )}
                         </div>
                         );
                       })}
                     </div>
                   </div>
                 );
               })()}
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-white/60">Harga (IDR)</label>
                <input data-testid="product-price-input" type="number" step="1000" required value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="50000" className="w-full bg-black border border-white/15 focus:border-[#00F0FF] p-3 text-sm mt-2" />
                {form.price ? (() => {
                  const cat = categories.find((c) => c.id === form.category);
                  const feePct = cat?.fee_pct || 0;
                  const gross = parseFloat(form.price) || 0;
                  const feeAmt = gross * (feePct / 100);
                  const modalNum = form.modal === "" || form.modal === null || form.modal === undefined ? 0 : parseFloat(form.modal);
                  const modalValid = Number.isFinite(modalNum) && modalNum > 0 ? modalNum : 0;
                  const net = gross - feeAmt;
                  const profit = modalValid > 0 ? net - modalValid : null;
                  const margin = modalValid > 0 ? (profit / gross) * 100 : null;
                  return (
                    <div className="mt-2 p-2 bg-black border border-white/10 text-[11px] space-y-0.5" data-testid="price-fee-preview">
                      <div className="flex justify-between text-white/60"><span>Harga jual</span><span>{formatIDR(gross)}</span></div>
                      <div className="flex justify-between text-[#FF6B6B]"><span>Fee admin ({feePct}%)</span><span>−{formatIDR(feeAmt)}</span></div>
                      <div className="flex justify-between text-[#00FF66] font-bold pt-1 border-t border-white/10"><span>Diterima seller</span><span>{formatIDR(net)}</span></div>
                      {profit !== null && (
                        <>
                          <div className="flex justify-between text-white/60 pt-1 border-t border-white/5"><span>Modal</span><span>−{formatIDR(modalValid)}</span></div>
                          <div className="flex justify-between text-[#FFD700] font-bold"><span>Estimasi profit</span><span>{formatIDR(profit)} ({margin.toFixed(1)}%)</span></div>
                        </>
                      )}
                    </div>
                  );
                })() : null}
              </div>
              <div>
                {/* Modal: harga pokok / cost. Field privat seller, tidak di-share ke
                   publik. Disimpan di document products.modal (opsional). Tujuannya
                   supaya seller bisa pantau margin & profit bersih per produk.
                   Backend hanya menerima nilai numerik >= 0; kosong = tidak ada modal. */}
                <label className="text-xs uppercase tracking-[0.2em] text-white/60" title="Hanya untukmu. Tidak ditampilkan ke publik.">
                  Modal (IDR) <span className="text-white/40 normal-case tracking-normal">— privat, hanya untukmu</span>
                </label>
                <input
                  data-testid="product-modal-input"
                  type="number"
                  step="1000"
                  min="0"
                  value={form.modal}
                  onChange={(e) => setForm({ ...form, modal: e.target.value })}
                  placeholder="opsional · mis. 25000"
                  className="w-full bg-black border border-white/15 focus:border-[#FFD700] p-3 text-sm mt-2"
                />
                {(() => {
                  const modalNum = form.modal === "" || form.modal === null || form.modal === undefined ? 0 : parseFloat(form.modal);
                  const modalValid = Number.isFinite(modalNum) && modalNum > 0 ? modalNum : 0;
                  if (modalValid > 0) {
                    return (
                      <p className="text-[10px] text-[#FFD700] mt-1 uppercase tracking-widest flex items-center gap-1" data-testid="modal-privacy-note">
                        🔒 Disimpan privat. Tidak terlihat di katalog publik.
                      </p>
                    );
                  }
                  return (
                    <p className="text-[10px] text-white/40 mt-1">Kosongkan kalau belum tahu. Bisa diisi nanti lewat Edit.</p>
                  );
                })()}
              </div>
              {/* Field Stock:
                  - Untuk produk INSTANT DELIVERY (voucher/akun), stok mengikuti
                    jumlah kode belum-sold. Karena itu, saat MENGEDIT produk
                    voucher/akun, field Stock DISEMBUNYIKAN dan diganti info
                    supaya seller mengubah stok lewat "Kelola Voucher/Akun".
                  - Untuk produk lain / saat membuat baru, field Stock tetap ada. */}
              {editing && ["voucher", "akun"].includes((form.type || "").toLowerCase()) ? (
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-white/60">Stock</label>
                  <div className="w-full bg-black border border-white/15 p-3 text-sm mt-2 text-white/50" data-testid="product-stock-locked">
                    Stok mengikuti jumlah kode. Ubah lewat tombol
                    {" "}
                    <span className="text-[#00FF66] font-bold">
                      {(form.type || "").toLowerCase() === "voucher" ? "Kelola Voucher" : "Kelola Akun"}
                    </span>.
                  </div>
                </div>
              ) : (
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-white/60">Stock</label>
                  <input data-testid="product-stock-input" type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="w-full bg-black border border-white/15 focus:border-[#00F0FF] p-3 text-sm mt-2" />
                </div>
              )}

              <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-[0.2em] text-white/60">Product Image (URL)</label>
                <p className="text-[10px] text-white/50 mt-1 mb-2">Upload foto Anda ke <a href="https://imgur.com" target="_blank" rel="noopener" className="text-[#00F0FF] hover:underline">imgur.com</a> / <a href="https://postimg.cc" target="_blank" rel="noopener" className="text-[#00F0FF] hover:underline">postimg.cc</a>, lalu paste link URL di sini.</p>
                <div className="mt-2 flex items-center gap-3">
                  {form.image_url && (
                    <div className="w-20 h-20 border border-white/15 overflow-hidden relative shrink-0">
                      <img src={resolveImg(form.image_url)} alt="preview" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, image_url: "" })}
                        className="absolute top-0 right-0 bg-black/70 text-white p-0.5"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </div>
                <input
                  data-testid="product-image-input"
                  value={form.image_url}
                  onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                  placeholder="https://i.imgur.com/xxxxx.jpg"
                  className="w-full bg-black border border-white/15 focus:border-[#00F0FF] p-3 text-sm mt-2"
                />
              </div>
              <div className="md:col-span-2">
                <button data-testid="product-save-btn" type="submit" className="btn-primary">
                  {editing ? "Update Product" : "Create Product"}
                </button>
              </div>
            </form>
          )}

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {products.length === 0 ? (
              <p className="text-white/50 col-span-full text-center py-8">No products yet.</p>
            ) : (
              products.map((p) => {
                // Hitung estimasi profit per item di listing seller. Modal adalah
                // field privat (tidak di-share publik) sehingga cukup ditampilkan
                // di sini sebagai referensi profit saja.
                const cat = categories.find((c) => c.id === p.category);
                const feePct = cat?.fee_pct || 0;
                const gross = parseFloat(p.price) || 0;
                const feeAmt = Math.round(gross * (feePct / 100));
                const net = gross - feeAmt;
                const modalNum = parseFloat(p.modal);
                const hasModal = Number.isFinite(modalNum) && modalNum > 0;
                const profit = hasModal ? net - modalNum : null;
                const margin = hasModal && gross > 0 ? (profit / gross) * 100 : null;
                return (
                <div key={p.id} className="border border-white/10 p-4 flex gap-4 items-center" data-testid={`seller-product-${p.id}`}>
                  <div className="w-16 h-16 bg-black border border-white/10 overflow-hidden shrink-0">
                    {p.image_url && <img src={resolveImg(p.image_url)} alt={p.title} className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-white text-sm truncate">{p.title}</h3>
                    <p className="text-[#00F0FF] font-bold text-sm">{formatIDR(p.price)}</p>
                    <p className="text-[10px] text-white/50 uppercase tracking-widest">{p.type} · stock {p.stock}</p>
                    {hasModal && (
                      <div
                        className="mt-1.5 flex items-center gap-2 text-[10px] uppercase tracking-widest"
                        data-testid={`seller-product-profit-${p.id}`}
                        title="Hanya kamu yang bisa melihat ini. Tidak di-share ke publik."
                      >
                        <span className="text-white/40">🔒 Modal</span>
                        <span className="text-white/60 font-mono">{formatIDR(modalNum)}</span>
                        <span className="text-white/30">→</span>
                        <span className="text-[#FFD700] font-bold">Profit {formatIDR(profit)}</span>
                        <span className={`font-mono ${margin >= 20 ? "text-[#00FF66]" : margin >= 0 ? "text-[#FFD700]" : "text-[#FF003C]"}`}>
                          ({margin.toFixed(0)}%)
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <button onClick={() => startEdit(p)} className="text-white/60 hover:text-[#00F0FF] p-1">
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button onClick={() => removeProduct(p.id)} className="text-white/60 hover:text-[#FF003C] p-1">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                );
              })
            )}
        </div>
        </div>
      )}

      {tab === "orders" && (
        <div className="space-y-3">
          {orders.length === 0 ? (
            <p className="text-white/50 text-center py-8">No paid orders yet.</p>
          ) : (
            orders.map((o) => (
              <div key={o.id} className="border border-white/10 p-4" data-testid={`seller-order-${o.id}`}>
                <div className="flex justify-between items-center mb-3">
                  <div>
                    <span className="text-[10px] uppercase tracking-widest text-white/40">Order</span>
                    <p className="font-mono text-xs text-white">{o.id.slice(0, 12)}...</p>
                  </div>
                  <span
                    className={`tag ${
                      o.status === "paid"
                        ? "tag-accent"
                        : o.status === "delivered"
                          ? "!border-[#FFD700]/50 !text-[#FFD700] !bg-[#FFD700]/10"
                          : "!border-[#00FF66]/50 !text-[#00FF66] !bg-[#00FF66]/10"
                    }`}
                  >
                    {o.status.toUpperCase()}
                  </span>
                </div>
                <div className="space-y-1 text-sm">
                  {o.items.map((it) => (
                    <div key={it.product_id} className="flex justify-between text-white/70">
                      <span>{it.title} × {it.quantity}</span>
                      <span>{formatIDR(it.price * it.quantity)}</span>
                    </div>
                  ))}
                </div>
                <div className="border-t border-white/10 mt-3 pt-3 flex items-center justify-between gap-2 flex-wrap">
                  <div>
                    <span className="text-xs uppercase tracking-widest text-white/60">Your Revenue</span>
                    <p className="font-display font-bold text-[#00F0FF]">{formatIDR(o.seller_total)}</p>
                    {o.warranty_until && o.delivery_status === "shipped" && (
                      <p className="text-[10px] text-[#FFD700] mt-1">Cair otomatis: {new Date(o.warranty_until).toLocaleString("id-ID")}</p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Link
                      to={`/orders/${o.id}`}
                      data-testid={`order-detail-${o.id}`}
                      className="inline-flex items-center gap-1 px-3 py-2 bg-[#FFD700] text-black hover:bg-[#FFC700] text-xs uppercase font-bold tracking-widest"
                    >
                      📄 Detail Order
                    </Link>
                    <button data-testid={`chat-buyer-${o.id}`} onClick={() => setChatModal(o)} className="inline-flex items-center gap-1 px-3 py-2 border border-[#00F0FF]/40 text-[#00F0FF] hover:bg-[#00F0FF]/10 text-xs uppercase font-bold tracking-widest">
                      💬 Chat Buyer
                    </button>
                    {o.status === "paid" && o.delivery_status !== "shipped" && (
                      <>
                        <button
                          data-testid={`mark-delivered-${o.id}`}
                          onClick={async () => {
                            try {
                              await api.post(`/orders/${o.id}/mark-delivered`);
                              toast.success("Pesanan ditandai dikirim. Garansi dimulai.");
                              refresh();
                            } catch (err) {
                              toast.error(err.response?.data?.detail || "Failed");
                            }
                          }}
                          className="inline-flex items-center gap-1 px-3 py-2 bg-[#00FF66] text-black hover:bg-[#33FF88] text-xs uppercase font-bold tracking-widest"
                        >
                          ✓ Pesanan Dikirim
                        </button>
                        <button
                          data-testid={`cancel-order-${o.id}`}
                          onClick={async () => {
                            const reason = window.prompt(
                              "Batalkan pesanan ini? Dana buyer akan dikembalikan ke wallet.\nAlasan (opsional):",
                              ""
                            );
                            if (reason === null) return;
                            if (!window.confirm("Yakin ingin membatalkan pesanan ini? Tindakan ini tidak dapat dibatalkan.")) return;
                            try {
                              await api.post(`/orders/${o.id}/cancel-by-seller`, { reason });
                              toast.success("Pesanan dibatalkan. Dana buyer telah dikembalikan ke wallet.");
                              refresh();
                            } catch (err) {
                              toast.error(err.response?.data?.detail || "Gagal membatalkan pesanan");
                            }
                          }}
                          className="inline-flex items-center gap-1 px-3 py-2 border border-[#FF003C]/60 text-[#FF003C] hover:bg-[#FF003C]/10 text-xs uppercase font-bold tracking-widest"
                        >
                          ✕ Batalkan Pesanan
                        </button>
                      </>
                    )}
                    {o.delivery_status === "shipped" && (
                      <span className="text-xs uppercase tracking-widest text-[#FFD700] px-3 py-2 border border-[#FFD700]/30">⏳ Garansi aktif</span>
                    )}
                    {o.delivery_status === "disputed" && (
                      <span className="text-xs uppercase tracking-widest text-[#FF003C] px-3 py-2 border border-[#FF003C]/30">⚠ Komplain</span>
                    )}
                    {o.delivery_status === "completed" && (
                      <span className="text-xs uppercase tracking-widest text-[#00FF66] px-3 py-2 border border-[#00FF66]/30">✓ Selesai</span>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {tab === "auctions" && <AuctionManager categories={categories} productTypes={productTypes} />}

      {tab === "withdraw" && <WithdrawTab user={user} />}

      {tab === "coupons" && (
        <div>
          <button
            data-testid="add-coupon-btn"
            onClick={() => setShowCouponForm((v) => !v)}
            className="btn-primary inline-flex items-center gap-2 mb-6"
          >
            <Tag className="w-4 h-4" />
            {showCouponForm ? "Close form" : "Create Coupon"}
          </button>
          {showCouponForm && (
            <form onSubmit={submitCoupon} className="border border-white/10 p-6 mb-6 grid md:grid-cols-2 gap-4" data-testid="coupon-form">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-white/60">Code</label>
                <input
                  data-testid="coupon-code-input"
                  required
                  value={couponForm.code}
                  onChange={(e) => setCouponForm({ ...couponForm, code: e.target.value.toUpperCase() })}
                  placeholder="SUMMER25"
                  className="w-full bg-black border border-white/15 focus:border-[#00F0FF] p-3 text-sm mt-2 uppercase"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-white/60">Discount type</label>
                <select
                  data-testid="coupon-type-input"
                  value={couponForm.discount_type}
                  onChange={(e) => setCouponForm({ ...couponForm, discount_type: e.target.value })}
                  className="w-full bg-black border border-white/15 focus:border-[#00F0FF] p-3 text-sm mt-2"
                >
                  <option value="percentage">Percentage (%)</option>
                  <option value="fixed">Fixed Amount (Rp)</option>
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-white/60">
                  Value {couponForm.discount_type === "percentage" ? "(%)" : "(Rp)"}
                </label>
                <input
                  data-testid="coupon-value-input"
                  type="number"
                  required
                  value={couponForm.value}
                  onChange={(e) => setCouponForm({ ...couponForm, value: e.target.value })}
                  className="w-full bg-black border border-white/15 focus:border-[#00F0FF] p-3 text-sm mt-2"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-white/60">Min. order (Rp)</label>
                <input
                  data-testid="coupon-min-input"
                  type="number"
                  value={couponForm.min_order_amount}
                  onChange={(e) => setCouponForm({ ...couponForm, min_order_amount: e.target.value })}
                  className="w-full bg-black border border-white/15 focus:border-[#00F0FF] p-3 text-sm mt-2"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-white/60">Max uses (optional)</label>
                <input
                  data-testid="coupon-maxuses-input"
                  type="number"
                  value={couponForm.max_uses}
                  onChange={(e) => setCouponForm({ ...couponForm, max_uses: e.target.value })}
                  placeholder="Unlimited"
                  className="w-full bg-black border border-white/15 focus:border-[#00F0FF] p-3 text-sm mt-2"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-white/60">Expires at (optional)</label>
                <input
                  data-testid="coupon-expires-input"
                  type="datetime-local"
                  value={couponForm.expires_at}
                  onChange={(e) => setCouponForm({ ...couponForm, expires_at: e.target.value ? new Date(e.target.value).toISOString() : "" })}
                  className="w-full bg-black border border-white/15 focus:border-[#00F0FF] p-3 text-sm mt-2"
                />
              </div>
              <div className="md:col-span-2">
                <button data-testid="coupon-save-btn" type="submit" className="btn-primary">
                  Create Coupon
                </button>
              </div>
            </form>
          )}
          <div className="space-y-2">
            {coupons.length === 0 ? (
              <p className="text-white/50 text-center py-8">No coupons yet.</p>
            ) : (
              coupons.map((c) => (
                <div key={c.id} className="border border-white/10 p-4 flex items-center gap-4" data-testid={`coupon-${c.code}`}>
                  <div className="font-mono text-lg text-[#00F0FF] font-bold">{c.code}</div>
                  <div className="flex-1">
                    <p className="text-sm text-white">
                      {c.discount_type === "percentage" ? `${c.value}% off` : `${formatIDR(c.value)} off`}
                    </p>
                    <p className="text-[10px] uppercase tracking-widest text-white/40">
                      Used {c.used_count}{c.max_uses ? `/${c.max_uses}` : ""} · Min order {formatIDR(c.min_order_amount || 0)}
                    </p>
                  </div>
                  <button onClick={() => removeCoupon(c.id)} className="text-white/60 hover:text-[#FF003C] p-2">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {tab === "analytics" && (
        <div data-testid="analytics-panel">
          {!analytics ? (
            <p className="text-white/50 text-center py-8">Loading analytics...</p>
          ) : (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <div className="border border-white/10 p-6">
                  <BarChart3 className="w-5 h-5 text-[#00F0FF] mb-3" />
                  <div className="font-display font-black text-3xl text-white">{formatIDR(analytics.total_revenue)}</div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mt-1">Revenue · 30d</div>
                </div>
                <div className="border border-white/10 p-6">
                  <DollarSign className="w-5 h-5 text-[#00FF66] mb-3" />
                  <div
                    className="font-display font-black text-3xl"
                    style={{ color: (analytics.total_net_profit || 0) >= 0 ? "#00FF66" : "#FF003C" }}
                    data-testid="analytics-net-profit"
                  >
                    {formatIDR(analytics.total_net_profit || 0)}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mt-1">Untung Bersih · 30d</div>
                  <div className="text-[10px] text-white/30 mt-1">
                    Modal {formatIDR(analytics.total_modal || 0)} · Fee {formatIDR(analytics.total_fee || 0)}
                  </div>
                </div>
                <div className="border border-white/10 p-6">
                  <ShoppingBag className="w-5 h-5 text-[#FFD700] mb-3" />
                  <div className="font-display font-black text-3xl text-white">{formatNumber(analytics.total_orders)}</div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mt-1">Orders · 30d</div>
                </div>
                <div className="border border-white/10 p-6">
                  <Package className="w-5 h-5 text-[#00F0FF] mb-3" />
                  <div className="font-display font-black text-3xl text-white">{formatNumber(analytics.total_items)}</div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mt-1">Items sold · 30d</div>
                </div>
              </div>

              <div className="border border-white/10 p-6 mb-8" data-testid="revenue-chart">
                <h3 className="font-display font-bold text-white mb-4">Revenue (last 30 days)</h3>
                <div style={{ width: "100%", height: 280 }}>
                  <ResponsiveContainer>
                    <AreaChart data={analytics.daily}>
                      <defs>
                        <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#00F0FF" stopOpacity={0.6} />
                          <stop offset="100%" stopColor="#00F0FF" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#222" vertical={false} />
                      <XAxis dataKey="date" tick={{ fill: "#999", fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                      <YAxis tick={{ fill: "#999", fontSize: 10 }} tickFormatter={(v) => (v >= 1000000 ? `${(v / 1000000).toFixed(1)}jt` : v >= 1000 ? `${(v / 1000).toFixed(0)}rb` : v)} />
                      <Tooltip
                        contentStyle={{ background: "#0a0a0a", border: "1px solid #222", fontSize: 12 }}
                        labelStyle={{ color: "#fff" }}
                        formatter={(v) => formatIDR(v)}
                      />
                      <Area type="monotone" dataKey="revenue" stroke="#00F0FF" strokeWidth={2} fill="url(#g1)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="border border-[#00FF66]/30 bg-[#00FF66]/5 p-6 mb-8" data-testid="net-profit-chart">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="w-4 h-4 text-[#00FF66]" />
                  <h3 className="font-display font-bold text-white">Untung Bersih (last 30 days)</h3>
                </div>
                <p className="text-[11px] text-white/55 mb-4">
                  Net profit harian = (pendapatan setelah fee admin) − total modal produk terjual.
                  {(analytics.items_with_modal || 0) < (analytics.total_items || 0) && (
                    <span className="text-[#FFD700] ml-1">
                      ⚠ Hanya {analytics.items_with_modal || 0} dari {analytics.total_items || 0} item punya modal terisi. Tambahkan modal di tiap produk untuk angka akurat.
                    </span>
                  )}
                </p>
                <div style={{ width: "100%", height: 280 }}>
                  <ResponsiveContainer>
                    <AreaChart data={analytics.daily}>
                      <defs>
                        <linearGradient id="gNet" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#00FF66" stopOpacity={0.6} />
                          <stop offset="100%" stopColor="#00FF66" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#222" vertical={false} />
                      <XAxis dataKey="date" tick={{ fill: "#999", fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                      <YAxis tick={{ fill: "#999", fontSize: 10 }} tickFormatter={(v) => (v >= 1000000 ? `${(v / 1000000).toFixed(1)}jt` : v >= 1000 ? `${(v / 1000).toFixed(0)}rb` : v)} />
                      <Tooltip
                        contentStyle={{ background: "#0a0a0a", border: "1px solid #222", fontSize: 12 }}
                        labelStyle={{ color: "#fff" }}
                        formatter={(v) => formatIDR(v)}
                      />
                      <Area type="monotone" dataKey="net_profit" stroke="#00FF66" strokeWidth={2} fill="url(#gNet)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="border border-white/10 p-6 mb-8">
                <h3 className="font-display font-bold text-white mb-4">Orders per day</h3>
                <div style={{ width: "100%", height: 220 }}>
                  <ResponsiveContainer>
                    <BarChart data={analytics.daily}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#222" vertical={false} />
                      <XAxis dataKey="date" tick={{ fill: "#999", fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                      <YAxis tick={{ fill: "#999", fontSize: 10 }} allowDecimals={false} />
                      <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #222", fontSize: 12 }} labelStyle={{ color: "#fff" }} />
                      <Bar dataKey="orders" fill="#FFD700" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="border border-white/10 p-6" data-testid="top-products-section">
                <h3 className="font-display font-bold text-white mb-4">Top products</h3>
                {analytics.top_products.length === 0 ? (
                  <p className="text-white/40 text-sm">No sales yet in this period.</p>
                ) : (
                  <div className="space-y-2">
                    {analytics.top_products.map((p, i) => {
                      const pNet = Number(p.net_profit || 0);
                      const pModal = Number(p.modal || 0);
                      const hasPModal = pModal > 0;
                      return (
                        <div key={p.product_id} data-testid={`top-product-${p.product_id}`} className="flex items-center gap-4 border-b border-white/10 last:border-0 pb-2">
                          <span className="font-display font-black text-[#00F0FF] w-6">{i + 1}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm truncate">{p.title}</p>
                            <p className="text-[10px] uppercase tracking-widest text-white/40">
                              {p.sold} sold · Revenue {formatIDR(p.revenue)}
                              {hasPModal && (
                                <>
                                  <span className="text-white/30 mx-1">·</span>
                                  <span className="text-[#FFD700]">Modal {formatIDR(pModal)}</span>
                                </>
                              )}
                            </p>
                          </div>
                          <div className="text-right shrink-0">
                            <div
                              className="font-bold text-sm"
                              style={{ color: hasPModal ? (pNet >= 0 ? "#00FF66" : "#FF003C") : "#FFD700" }}
                              data-testid={`top-product-profit-${p.product_id}`}
                            >
                              {hasPModal ? formatIDR(pNet) : formatIDR(p.revenue)}
                            </div>
                            <div className="text-[9px] uppercase tracking-widest text-white/40">
                              {hasPModal ? "Untung Bersih" : "Revenue"}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {showRequestModal && <RequestProductModal onClose={() => setShowRequestModal(false)} />}
      {manageCodes && (
        <ManageCodesModal
          kind={manageCodes.kind}
          product={manageCodes.product}
          products={products.filter((p) => (p.type || "").toLowerCase() === manageCodes.kind)}
          productTypes={productTypes}
          onPickProduct={(prod) => setManageCodes({ ...manageCodes, product: prod })}
          onClose={() => setManageCodes(null)}
          onSaved={() => { refresh(); }}
        />
      )}
      {chatModal && <OrderChatPanel order={chatModal} currentUserId={user.id} onClose={() => setChatModal(null)} />}

    </div>
  );
}

function RequestProductModal({ onClose }) {
  const [form, setForm] = useState({ title: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [myRequests, setMyRequests] = useState([]);

  const refresh = async () => {
    try {
      const { data } = await api.get("/product-requests/me");
      setMyRequests(data);
    } catch {}
  };
  useEffect(() => { refresh(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.message.trim()) { toast.error("Judul & pesan wajib diisi"); return; }
    setBusy(true);
    try {
      await api.post("/product-requests", form);
      toast.success("Request berhasil dikirim. Admin akan respon di sini.");
      setForm({ title: "", message: "" });
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#050505] border border-white/15 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="request-product-modal">
        <div className="p-6 border-b border-white/10 flex items-start justify-between">
          <div>
            <h3 className="font-display font-black text-2xl text-white">Request Produk</h3>
            <p className="text-xs text-white/50 mt-1">Ajukan game/kategori baru ke admin. Setelah disetujui, kamu bisa pasang produknya.</p>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <form onSubmit={submit} className="p-6 border-b border-white/10 space-y-3">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-white/60">Judul Request</label>
            <input data-testid="req-title" required placeholder="Mis. Tambah game Honkai Star Rail" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full bg-black border border-white/15 focus:border-[#FFD700] p-3 text-sm mt-2" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-white/60">Detail / Alasan</label>
            <textarea data-testid="req-message" required rows={5} placeholder="Jelaskan kategori produk yang mau dijual, kenapa perlu ditambah, dll..." value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full bg-black border border-white/15 focus:border-[#FFD700] p-3 text-sm mt-2" />
          </div>
          <button data-testid="req-submit" disabled={busy} type="submit" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#FFD700] hover:bg-[#FFC700] text-black text-xs uppercase tracking-widest font-bold disabled:opacity-50">
            <Send className="w-4 h-4" /> {busy ? "Mengirim..." : "Kirim Request"}
          </button>
        </form>

        <div className="p-6">
          <h4 className="text-xs uppercase tracking-[0.2em] text-white/60 mb-3">Riwayat Request Saya</h4>
          {myRequests.length === 0 ? (
            <p className="text-white/40 text-xs text-center py-6">Belum ada request.</p>
          ) : (
            <div className="space-y-2">
              {myRequests.map((r) => (
                <div key={r.id} className="border border-white/10 p-3" data-testid={`my-req-${r.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-bold text-white text-sm flex-1">{r.title}</p>
                    <span className={`tag ${r.status === "resolved" ? "tag-accent" : r.status === "rejected" ? "" : ""}`} style={r.status === "rejected" ? { borderColor: "rgba(255,0,60,0.6)", color: "#FF003C", background: "rgba(255,0,60,0.1)" } : r.status === "pending" ? { borderColor: "rgba(255,215,0,0.6)", color: "#FFD700", background: "rgba(255,215,0,0.1)" } : {}}>{r.status}</span>
                  </div>
                  <p className="text-white/60 text-xs mt-1.5 whitespace-pre-wrap">{r.message}</p>
                  {r.admin_response && (
                    <div className="mt-2 pt-2 border-t border-white/10 text-xs">
                      <span className="text-[#00F0FF] uppercase tracking-widest text-[10px]">Admin:</span> <span className="text-white/80">{r.admin_response}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


const BANK_LIST = ["BCA", "BNI", "BRI", "Mandiri", "CIMB Niaga", "Permata", "Danamon", "BTN", "BSI", "Maybank", "OCBC NISP", "Panin", "Mega", "Bank Jago", "SeaBank", "Bank Neo", "DANA", "OVO", "GoPay"];

function WithdrawTab({ user }) {
  const wallet = useWallet();
  const [form, setForm] = useState({ amount: "", bank_name: "BCA", account_number: "", account_name: "", pin: "" });
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [transferModal, setTransferModal] = useState(false);
  const [transferAmount, setTransferAmount] = useState("");
  const [transferPin, setTransferPin] = useState("");

  const refresh = async () => {
    try {
      const { data } = await api.get("/wallet/withdrawals");
      setHistory(data);
    } catch { /* noop */ }
  };
  useEffect(() => { refresh(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isBCA = form.bank_name.toUpperCase() === "BCA";
  const fee = isBCA ? 0 : 6500;
  const amountNum = parseInt(form.amount || 0, 10);
  const net = Math.max(0, amountNum - fee);

  const submit = async (e) => {
    e.preventDefault();
    if (amountNum < 10000) { toast.error("Minimum penarikan Rp 10.000"); return; }
    if (amountNum > (wallet.seller_balance || 0)) { toast.error("Saldo seller tidak cukup"); return; }
    if (form.pin.length !== 6) { toast.error("PIN harus 6 digit"); return; }
    setLoading(true);
    try {
      await api.post("/wallet/withdraw", {
        amount: amountNum,
        bank_name: form.bank_name,
        account_number: form.account_number,
        account_name: form.account_name,
        pin: form.pin,
      });
      toast.success("Penarikan diajukan. Admin akan memproses transfer.");
      setForm({ amount: "", bank_name: "BCA", account_number: "", account_name: "", pin: "" });
      wallet.refresh && wallet.refresh();
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="withdraw-tab">
      <h2 className="font-display font-black text-2xl text-white">Penarikan Saldo Seller</h2>
      <p className="text-xs text-white/50 mt-1">Tarik pendapatan dari penjualan produk ke rekening bank Anda.</p>

      <div className="mt-4 border border-[#00F0FF]/30 bg-[#00F0FF]/5 p-4 flex items-center gap-3 flex-wrap" data-testid="transfer-banner">
        <div className="flex-1 min-w-[240px]">
          <p className="text-xs uppercase tracking-widest text-[#00F0FF]">⚡ Pindah ke Saldo Buyer (Gratis)</p>
          <p className="text-[11px] text-white/60 mt-1">Mau belanja di Ustore? Pindahkan saldo seller ke saldo buyer untuk checkout produk lain. Bebas biaya.</p>
        </div>
        <button data-testid="open-transfer-btn" onClick={() => { setTransferModal(true); setTransferAmount(""); setTransferPin(""); }} className="px-4 py-2 bg-[#00F0FF] text-black hover:bg-[#33F0FF] text-xs uppercase font-bold tracking-widest">
          Pindahkan
        </button>
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-6 mt-6">
        {/* Form */}
        <form onSubmit={submit} className="border border-[#00FF66]/30 bg-[#00FF66]/5 p-6 space-y-3" data-testid="withdraw-form">
          <div className="flex items-center justify-between border-b border-white/10 pb-3 mb-2">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-white/50">Saldo Tersedia</p>
              <p className="font-display font-black text-2xl text-[#00FF66]" data-testid="withdraw-seller-balance">{formatIDR(wallet.seller_balance || 0)}</p>
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-white/60">Jumlah (min Rp 10.000)</label>
            <input
              data-testid="withdraw-amount"
              type="number" min="10000" required
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              placeholder="50000"
              className="w-full bg-black border border-white/15 focus:border-[#00FF66] p-3 text-sm mt-1"
            />
            {form.amount && <p className="text-[10px] text-white/40 mt-1">{formatIDR(amountNum)}</p>}
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-white/60">Bank Tujuan</label>
            <select
              data-testid="withdraw-bank"
              value={form.bank_name}
              onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
              className="w-full bg-black border border-white/15 focus:border-[#00FF66] p-3 text-sm mt-1"
            >
              {BANK_LIST.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-white/60">No. Rekening</label>
              <input data-testid="withdraw-acc-no" required value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} placeholder="1234567890" className="w-full bg-black border border-white/15 focus:border-[#00FF66] p-3 text-sm mt-1" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-white/60">Nama Pemilik</label>
              <input data-testid="withdraw-acc-name" required value={form.account_name} onChange={(e) => setForm({ ...form, account_name: e.target.value })} placeholder="Nama sesuai rekening" className="w-full bg-black border border-white/15 focus:border-[#00FF66] p-3 text-sm mt-1" />
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-white/60">PIN Transaksi (6 digit)</label>
            <input data-testid="withdraw-pin" type="password" inputMode="numeric" maxLength="6" required value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "") })} className="w-full bg-black border border-white/15 focus:border-[#00FF66] p-3 text-sm mt-1 tracking-[0.5em]" />
          </div>

          {amountNum > 0 && (
            <div className="border border-white/10 p-3 text-xs space-y-1" data-testid="withdraw-preview">
              <div className="flex justify-between"><span className="text-white/60">Jumlah</span><span className="text-white">{formatIDR(amountNum)}</span></div>
              <div className="flex justify-between"><span className="text-white/60">Fee admin {isBCA ? "(BCA GRATIS)" : `(${form.bank_name})`}</span><span className={isBCA ? "text-[#00FF66]" : "text-[#FF003C]"}>{isBCA ? "Rp 0" : `− ${formatIDR(fee)}`}</span></div>
              <div className="flex justify-between font-bold border-t border-white/10 pt-1 mt-1"><span className="text-white">Diterima</span><span className="text-[#00FF66]" data-testid="withdraw-net">{formatIDR(net)}</span></div>
            </div>
          )}

          <button data-testid="withdraw-submit" type="submit" disabled={loading || amountNum < 10000} className="w-full bg-[#00FF66] text-black hover:bg-[#33FF88] disabled:opacity-50 font-display font-black uppercase tracking-widest px-6 py-3 text-sm">
            {loading ? "Memproses..." : "Ajukan Penarikan"}
          </button>
          <p className="text-[10px] text-white/40 text-center">Saldo dipotong saat diajukan. Admin akan transfer manual & tandai selesai (estimasi 1-24 jam jam kerja).</p>
        </form>

        {/* History */}
        <div className="border border-white/10 p-4 max-h-[600px] overflow-y-auto">
          <h3 className="text-xs uppercase tracking-widest text-white/60 mb-3">Riwayat Penarikan</h3>
          {history.length === 0 ? (
            <p className="text-white/40 text-xs text-center py-6">Belum ada riwayat</p>
          ) : (
            <div className="space-y-2">
              {history.map((r) => {
                const statusColor = r.status === "completed" ? "#00FF66" : r.status === "rejected" ? "#FF003C" : "#FFD700";
                return (
                  <div key={r.id} className="border border-white/10 p-3" data-testid={`withdraw-history-${r.id}`}>
                    <div className="flex justify-between items-center mb-1">
                      <p className="font-display font-bold text-sm text-white">{formatIDR(r.amount)}</p>
                      <span className="text-[10px] uppercase tracking-widest px-2 py-0.5" style={{ color: statusColor, border: `1px solid ${statusColor}`, background: `${statusColor}1a` }}>{r.status}</span>
                    </div>
                    <p className="text-[10px] text-white/60">{r.bank_name} · {r.account_number} · {r.account_name}</p>
                    <p className="text-[10px] text-white/40 mt-1">{new Date(r.created_at).toLocaleString("id-ID")}</p>
                    {r.admin_note && <p className="text-[10px] text-[#00FF66] mt-1">✓ {r.admin_note}</p>}
                    {r.rejection_reason && <p className="text-[10px] text-[#FF003C] mt-1">✗ {r.rejection_reason}</p>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {transferModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="transfer-modal">
          <div className="w-full max-w-md border border-[#00F0FF]/40 bg-[#0a0a0a] p-6 space-y-3">
            <h3 className="font-display font-black text-xl text-white">Pindah ke Saldo Buyer</h3>
            <p className="text-xs text-white/60">Pindahkan saldo seller agar bisa dipakai checkout produk lain. <span className="text-[#00FF66] font-bold">Gratis tanpa fee.</span></p>
            <div className="grid grid-cols-2 gap-2 text-xs border-y border-white/10 py-2">
              <div><p className="text-white/50 text-[10px] uppercase tracking-widest">Saldo Seller</p><p className="text-[#00FF66] font-bold">{formatIDR(wallet.seller_balance || 0)}</p></div>
              <div><p className="text-white/50 text-[10px] uppercase tracking-widest">Saldo Buyer</p><p className="text-[#00F0FF] font-bold">{formatIDR(wallet.balance || 0)}</p></div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-white/60">Jumlah (min Rp 10.000)</label>
              <input data-testid="transfer-amount" type="number" min="10000" value={transferAmount} onChange={(e) => setTransferAmount(e.target.value)} placeholder="50000" className="w-full bg-black border border-white/15 focus:border-[#00F0FF] p-3 text-sm mt-1" />
              {transferAmount && <p className="text-[10px] text-white/40 mt-1">{formatIDR(parseInt(transferAmount, 10) || 0)}</p>}
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-white/60">PIN Transaksi (6 digit)</label>
              <input data-testid="transfer-pin" type="password" inputMode="numeric" maxLength="6" value={transferPin} onChange={(e) => setTransferPin(e.target.value.replace(/\D/g, ""))} className="w-full bg-black border border-white/15 focus:border-[#00F0FF] p-3 text-sm mt-1 tracking-[0.5em]" />
            </div>
            <div className="flex gap-2 pt-2">
              <button
                data-testid="transfer-submit"
                onClick={async () => {
                  const amt = parseInt(transferAmount, 10) || 0;
                  if (amt < 10000) { toast.error("Minimum Rp 10.000"); return; }
                  if (transferPin.length !== 6) { toast.error("PIN harus 6 digit"); return; }
                  try {
                    await api.post("/wallet/transfer-to-buyer", { amount: amt, pin: transferPin });
                    toast.success(`${formatIDR(amt)} dipindahkan ke saldo buyer`);
                    setTransferModal(false);
                    wallet.refresh && wallet.refresh();
                  } catch (err) { toast.error(err.response?.data?.detail || "Gagal"); }
                }}
                className="flex-1 px-4 py-3 bg-[#00F0FF] text-black hover:bg-[#33F0FF] font-display font-black uppercase tracking-widest text-xs"
              >
                Pindahkan Sekarang
              </button>
              <button onClick={() => setTransferModal(false)} className="px-4 py-3 border border-white/15 text-white/70 text-xs uppercase">Batal</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}



// SUB_CATEGORIES_AUC dulu hardcode — sekarang dibangun dinamis dari state
// `productTypes` (diambil via /api/product-types) supaya label garansi di dropdown
// seller = pengaturan admin. Bisa dipakai di banyak tempat dengan memo ringan.
// Fungsi murni: menerima productTypes + key "hari"/"jam"/"menit", return {value,label}.
function buildSubCategoryOptions(productTypes = []) {
  return productTypes.map((t) => {
    const d = Number(t.warranty_days) || 0;
    let dur = '';
    if (d >= 1 && d % 1 === 0) {
      // t.warranty_days sebenarnya integer hari. Kalau < 1, perlakukan sebagai jam.
      if (d >= 1) dur = `garansi ${d} hari`;
      else dur = `garansi ${Math.round(d * 24)} jam`;
    } else {
      dur = `garansi ${d} hari`;
    }
    return { value: t.id, label: `${t.label} (${dur})` };
  });
}
const FALLBACK_SUB_CATEGORIES_AUC = [
  { value: 'akun', label: 'Akun (garansi 7 hari)' },
  { value: 'topup_via_id', label: 'Top-up via ID (garansi 1 hari)' },
  { value: 'topup_via_login', label: 'Top-up via Login (garansi 7 hari)' },
  { value: 'voucher', label: 'Voucher (garansi 6 jam)' },
  { value: 'joki', label: 'Joki (garansi 7 hari)' },
];

const emptyAuction = {
  title: '',
  description: '',
  image_url: '',
  game_name: '',
  sub_category: 'akun',
  starting_bid: 50000,
  bid_increment: '',
  buy_now_price: '',
  account_details: '',
  duration_hours: 24,
};

const BID_INCREMENT_OPTIONS = [
  { value: '', label: 'Default (5% atau Rp 5.000)' },
  { value: 100, label: 'Rp 100' },
  { value: 500, label: 'Rp 500' },
  { value: 1000, label: 'Rp 1.000' },
  { value: 10000, label: 'Rp 10.000' },
  { value: 50000, label: 'Rp 50.000' },
  { value: 100000, label: 'Rp 100.000' },
  { value: 500000, label: 'Rp 500.000' },
  { value: 1000000, label: 'Rp 1.000.000' },
];

// Menerima `productTypes` dari SellerDashboard (sumber kebenaran = admin).
// Label subkategori + garansi pada dropdown auction seller diambil dari sini,
// bukan hardcode. Kalau productTypes belum dimuat, pakai fallback statis.
function AuctionManager({ productTypes = [] }) {
  const [auctions, setAuctions] = useState([]);
  const [allowedGames, setAllowedGames] = useState([]);
  const [form, setForm] = useState(emptyAuction);
  const [showForm, setShowForm] = useState(false);

  const refresh = async () => {
    const { data } = await api.get('/auctions/seller');

    setAuctions(data);
  };

  const refreshGames = async () => {
    const { data } = await api.get('/auction-games');
    setAllowedGames(data);
  };

  useEffect(() => { refresh(); refreshGames(); }, []);

  // Auto-pick first allowed sub_category for selected game
  const selectedGame = allowedGames.find((g) => g.game_name === form.game_name);
  const allowedSubs = selectedGame?.sub_categories || [];
  // Build opsi dropdown dari productTypes (admin). Fallback ke hardcode kalau belum load.
  const subCategoryOptions =
    productTypes && productTypes.length > 0
      ? buildSubCategoryOptions(productTypes)
      : FALLBACK_SUB_CATEGORIES_AUC;

  const submit = async (e) => {

    e.preventDefault();
    const payload = {
      ...form,
      starting_bid: parseFloat(form.starting_bid),
      bid_increment: form.bid_increment ? parseFloat(form.bid_increment) : null,
      buy_now_price: form.buy_now_price ? parseFloat(form.buy_now_price) : null,
      account_details: form.account_details.trim() || null,
      duration_hours: parseInt(form.duration_hours) || 24,
    };
    try {
      await api.post('/auctions', payload);
      toast.success('Auction created!');
      setForm(emptyAuction);
      setShowForm(false);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const closeAuction = async (id) => {
    if (!window.confirm('Force close auction ini?')) return;
    try {
      await api.post(`/auctions/${id}/close`);
      toast.success('Auction closed');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  return (
    <div>
      <button
        data-testid='add-auction-btn'
        onClick={() => setShowForm((v) => !v)}
        className='btn-primary inline-flex items-center gap-2 mb-6'
      >
        <Gavel className='w-4 h-4' /> {showForm ? 'Tutup form' : 'Buat Auction Baru'}
      </button>

      {showForm && (
        <form onSubmit={submit} className='border border-white/10 p-6 mb-6 grid md:grid-cols-2 gap-4' data-testid='auction-form'>
          {allowedGames.length === 0 && (
            <div className='md:col-span-2 border border-[#FFD700]/40 bg-[#FFD700]/5 p-3 text-xs text-[#FFD700]' data-testid='no-games-warning'>
              ⚠ Belum ada game yang diizinkan admin untuk auction. Hubungi admin untuk menambahkan game.
            </div>
          )}
          <div className='md:col-span-2'>
            <label className='text-xs uppercase tracking-[0.2em] text-white/60'>Nama Game</label>
            <select data-testid='auc-game-name' required value={form.game_name} onChange={(e) => {
              const g = allowedGames.find((x) => x.game_name === e.target.value);
              const firstSub = g?.sub_categories?.[0] || 'akun';
              setForm({ ...form, game_name: e.target.value, sub_category: firstSub });
            }} className='w-full bg-black border border-white/15 focus:border-[#7C6EFF] p-3 text-sm mt-2'>
              <option value=''>-- Pilih Game --</option>
              {allowedGames.map((g) => (
                <option key={g.game_id} value={g.game_name}>{g.game_name}</option>
              ))}
            </select>
            <p className='text-[10px] text-white/40 mt-1'>Hanya game yang sudah disetujui admin yang bisa dipilih.</p>
          </div>
          <div className='md:col-span-2'>
            <label className='text-xs uppercase tracking-[0.2em] text-white/60'>Judul auction</label>
            <input data-testid='auc-title' required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder='Akun ML Mythic Glory 800 star...' className='w-full bg-black border border-white/15 focus:border-[#7C6EFF] p-3 text-sm mt-2' />
          </div>
          <div>
            <label className='text-xs uppercase tracking-[0.2em] text-white/60'>Kategori</label>
            <select data-testid='auc-sub-category' value={form.sub_category} onChange={(e) => setForm({ ...form, sub_category: e.target.value })} disabled={!form.game_name} className='w-full bg-black border border-white/15 focus:border-[#7C6EFF] p-3 text-sm mt-2 disabled:opacity-50'>
              {/* Opsi dibangun dari productTypes (admin) — lihat subCategoryOptions.
                  Kalau admin izinkan sub_category khusus untuk game ini, filter sesuai.
                  Karena id admin (mis. "voucher") tidak selalu cocok dengan slug lama
                  (juga "voucher"), filter slug-compat dulu sebelum cek allowedSubs. */}
              {(allowedSubs.length
                ? subCategoryOptions.filter((s) => {
                    // Ambil slug yang kompatibel dengan sistem lama: kalau value
                    // mengandung underscore / alias, cocokkan secara longgar.
                    const slugCandidates = [s.value, String(s.value).replace(/_/g, '-')];
                    return allowedSubs.some((sub) => slugCandidates.includes(sub) || String(sub).includes(String(s.value)));
                  })
                : subCategoryOptions
              ).map((s) => (<option key={s.value} value={s.value}>{s.label}</option>))}
            </select>
            {form.game_name && allowedSubs.length > 0 && (
              <p className='text-[10px] text-white/40 mt-1'>Kategori yang diizinkan untuk {form.game_name}: {allowedSubs.join(', ')}</p>
            )}
          </div>
          <div>
            <label className='text-xs uppercase tracking-[0.2em] text-white/60'>Durasi (jam, default 24)</label>
            <input data-testid='auc-duration' type='number' min='1' max='168' value={form.duration_hours} onChange={(e) => setForm({ ...form, duration_hours: e.target.value })} className='w-full bg-black border border-white/15 focus:border-[#7C6EFF] p-3 text-sm mt-2' />
          </div>
          <div>
            <label className='text-xs uppercase tracking-[0.2em] text-white/60'>Starting bid (Rp)</label>
            <input data-testid='auc-start' type='number' required value={form.starting_bid} onChange={(e) => setForm({ ...form, starting_bid: e.target.value })} className='w-full bg-black border border-white/15 focus:border-[#7C6EFF] p-3 text-sm mt-2' />
            <p className='text-[10px] text-white/40 mt-1'>{formatIDR(form.starting_bid || 0)}</p>
          </div>
          <div>
            <label className='text-xs uppercase tracking-[0.2em] text-white/60'>Kelipatan Bid</label>
            <select
              data-testid='auc-bid-increment'
              value={form.bid_increment}
              onChange={(e) => setForm({ ...form, bid_increment: e.target.value })}
              className='w-full bg-black border border-white/15 focus:border-[#7C6EFF] p-3 text-sm mt-2'
            >
              {BID_INCREMENT_OPTIONS.map((o) => (
                <option key={o.value || 'default'} value={o.value}>{o.label}</option>
              ))}
            </select>
            <p className='text-[10px] text-white/40 mt-1'>
              {form.bid_increment
                ? `Bidder hanya bisa naikkan ${formatIDR(form.bid_increment)} per bid.`
                : 'Default: 5% dari current bid atau Rp 5.000 (mana yang lebih besar).'}
            </p>
          </div>
          <div>
            <label className='text-xs uppercase tracking-[0.2em] text-white/60'>Buy now price (opsional)</label>
            <input data-testid='auc-buy-now' type='number' value={form.buy_now_price} onChange={(e) => setForm({ ...form, buy_now_price: e.target.value })} placeholder='Kosongkan kalau tidak pakai' className='w-full bg-black border border-white/15 focus:border-[#7C6EFF] p-3 text-sm mt-2' />
          </div>
          {form.game_name && (
            <div className='md:col-span-2 border border-[#00FF66]/30 bg-[#00FF66]/5 p-3 text-xs' data-testid='auc-fee-preview'>
              {(() => {
                const feePct = Number(selectedGame?.fee_pct || 0);
                const gross = parseFloat(form.buy_now_price || form.starting_bid || 0);
                const feeAmount = Math.round(gross * feePct / 100);
                const net = gross - feeAmount;
                const refLabel = form.buy_now_price ? 'Buy Now' : 'Starting Bid';
                return (
                  <div className='space-y-1'>
                    <div className='flex justify-between text-white/70'>
                      <span>Fee admin untuk {form.game_name}</span>
                      <span className='text-[#FFD700] font-bold' data-testid='auc-fee-pct'>{feePct}%</span>
                    </div>
                    <div className='flex justify-between text-white/70'>
                      <span>{refLabel}</span>
                      <span>{formatIDR(gross)}</span>
                    </div>
                    <div className='flex justify-between text-white/70'>
                      <span>Potongan fee</span>
                      <span className='text-[#FF003C]'>− {formatIDR(feeAmount)}</span>
                    </div>
                    <div className='flex justify-between font-bold pt-1 border-t border-white/10 mt-1'>
                      <span className='text-white'>Estimasi cair ke saldo</span>
                      <span className='text-[#00FF66]' data-testid='auc-net-payout'>{formatIDR(net)}</span>
                    </div>
                    <p className='text-[10px] text-white/40 pt-1'>Dihitung jika auction menang di harga {refLabel.toLowerCase()}. Cair ke saldo setelah masa garansi selesai.</p>
                  </div>
                );
              })()}
            </div>
          )}
          <div className='md:col-span-2'>
            <label className='text-xs uppercase tracking-[0.2em] text-white/60'>Deskripsi</label>
            <textarea data-testid='auc-description' required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className='w-full bg-black border border-white/15 focus:border-[#7C6EFF] p-3 text-sm mt-2' />
          </div>
          <div className='md:col-span-2'>
            <label className='text-xs uppercase tracking-[0.2em] text-white/60'>Foto auction (URL)</label>
            <p className='text-[10px] text-white/50 mt-1 mb-2'>Upload foto Anda ke <a href='https://imgur.com' target='_blank' rel='noopener' className='text-[#00F0FF] hover:underline'>imgur.com</a> / <a href='https://postimg.cc' target='_blank' rel='noopener' className='text-[#00F0FF] hover:underline'>postimg.cc</a>, lalu paste link URL di sini.</p>
            <div className='mt-2 flex items-center gap-3'>
              {form.image_url && (
                <div className='w-20 h-20 border border-white/15 overflow-hidden relative shrink-0'>
                  <img src={form.image_url.startsWith('http') ? form.image_url : `${API.replace(/\/api$/, '')}${form.image_url}`} alt='' className='w-full h-full object-cover' onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                </div>
              )}
            </div>
            <input
              data-testid='auc-image-input'
              value={form.image_url}
              onChange={(e) => setForm({ ...form, image_url: e.target.value })}
              placeholder='https://i.imgur.com/xxxxx.jpg'
              className='w-full bg-black border border-white/15 focus:border-[#7C6EFF] p-3 text-sm mt-2'
            />
          </div>
          <div className='md:col-span-2'>
            <label className='text-xs uppercase tracking-[0.2em] text-white/60'>Detail akun (opsional · jadi INSTANT DELIVERY ⚡)</label>
            <textarea data-testid='auc-details' value={form.account_details} onChange={(e) => setForm({ ...form, account_details: e.target.value })} rows={4} placeholder='Email: account@gmail.com\nPassword: SECRETPWD\nBackup: 087xxxx\n\n(Hanya pemenang yang bisa lihat setelah menang)' className='w-full bg-black border border-white/15 focus:border-[#00FF66] p-3 text-sm font-mono mt-2' />
            {form.account_details && (
              <p className='text-[10px] uppercase tracking-widest text-[#00FF66] mt-2 flex items-center gap-1'>
                <Zap className='w-3 h-3' /> Auction ini akan dapat label INSTANT DELIVERY (warna hijau)
              </p>
            )}
          </div>
          <div className='md:col-span-2'>
            <button data-testid='auc-save' type='submit' className='btn-primary'>Publish Auction</button>
          </div>
        </form>
      )}

      <div className='space-y-3'>
        {auctions.length === 0 ? (
          <p className='text-white/50 text-center py-8'>Belum ada auction.</p>
        ) : (
          auctions.map((a) => (
            <div key={a.id} className='border border-white/10 p-4 flex items-center gap-4' data-testid={`seller-auction-${a.id}`}>
              <div className='w-16 h-16 bg-black border border-white/10 overflow-hidden shrink-0'>
                {a.image_url && <img src={a.image_url.startsWith('http') ? a.image_url : `${API.replace(/\/api$/, '')}${a.image_url}`} alt='' className='w-full h-full object-cover' />}
              </div>
              <div className='flex-1 min-w-0'>
                <h3 className='font-bold text-white text-sm truncate'>{a.title}</h3>
                <p className='text-[#00F0FF] font-bold text-sm'>{formatIDR(a.current_bid_amount || a.starting_bid)} · {a.bid_count} bids</p>
                <p className='text-[10px] text-white/50 uppercase tracking-widest'>{a.game_name} · {a.sub_category} · {a.status}</p>
                {typeof a.est_net_payout === 'number' && (
                  <p className='text-[10px] text-[#00FF66] mt-1' data-testid={`seller-auction-net-${a.id}`}>
                    Estimasi cair: <span className='font-bold'>{formatIDR(a.est_net_payout)}</span>
                    <span className='text-white/40'> (fee {a.seller_fee_pct || 0}%)</span>
                  </p>
                )}
              </div>
              {(a.status === 'live' || a.status === 'frozen') && (
                <button onClick={() => closeAuction(a.id)} className='text-xs px-3 py-2 border border-white/15 hover:border-[#FF003C] text-white/70 hover:text-[#FF003C] uppercase tracking-widest'>Close</button>
              )}
              {(a.status === 'expired' || a.status === 'cancelled') && a.bid_count === 0 && !a.relisted_to && (
                <button
                  data-testid={`relist-auction-${a.id}`}
                  onClick={async () => {
                    if (!window.confirm("Re-list lelang ini? Akan dibuat lelang BARU dengan detail sama, durasi 48 jam.")) return;
                    try {
                      await api.post(`/auctions/${a.id}/relist`, { duration_hours: 48 });
                      toast.success("Lelang berhasil di-relist!");
                      refresh();
                    } catch (err) { toast.error(err.response?.data?.detail || "Gagal relist"); }
                  }}
                  className='text-xs px-3 py-2 border border-[#00FF66]/50 text-[#00FF66] hover:bg-[#00FF66]/10 uppercase font-bold tracking-widest'
                >
                  🔄 Re-Auction
                </button>
              )}
              {a.relisted_to && (
                <span className='text-[10px] text-white/40 uppercase tracking-widest px-2 py-1 border border-white/10'>Sudah di-relist</span>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}


// ============================================================================
// ManageCodesModal — Kelola stok kode Voucher / detail Akun setelah produk rilis
// ----------------------------------------------------------------------------
// Menyelesaikan 2 masalah di ubidstore.com:
//   1) Data instant delivery yang tidak tersimpan saat post produk.
//   2) Tidak bisa diedit setelah produk di-post.
// Dibuka lewat tombol "Kelola Voucher" / "Kelola Akun" di tab Products.
// Menggunakan endpoint yang SUDAH ada di backend:
//   GET  /products/{id}/codes  -> { available:[{id,code,status}], sold:[...] }
//   PUT  /products/{id}/codes  -> { codes:[...] }  (kode sold otomatis dijaga)
// Logic per kind:
//   - "voucher" : textarea 1 baris = 1 kode (kompatibel dengan format lama).
//   - "akun"    : TABEL per-baris dengan kolom = field "instant_delivery_fields"
//                 yang diatur admin di ProductType. Konsisten dengan form Create
//                 Produk (bagian Instant Delivery Fields) sehingga seller tidak
//                 bingung beda format. Setiap baris = 1 akun; kolom = field.
//                 Pada save, tiap baris digabung dengan "|" lalu dikirim ke
//                 endpoint /codes (kode jadi string "email|password|login_via").
// ============================================================================
function ManageCodesModal({ kind, product, products = [], productTypes = [], onPickProduct, onClose, onSaved }) {
  const isVoucher = kind === "voucher";
  const accent = isVoucher ? "#00FF66" : "#00F0FF";
  const title = isVoucher ? "Kelola Voucher" : "Kelola Akun";

  // Cari definisi field untuk tipe produk "akun" dari productTypes (admin).
  // Fallback: kalau admin belum set, pakai default umum.
  const akunTypeDef = (productTypes || []).find(
    (t) => (t.id || "").toLowerCase() === "akun" || (t.label || "").toLowerCase() === "akun"
  );
  const akunFields =
    (akunTypeDef?.instant_delivery_fields && akunTypeDef.instant_delivery_fields.length > 0)
      ? akunTypeDef.instant_delivery_fields
      : ["Email", "Password", "Login Via"];

  // ===== State voucher =====
  const [codesText, setCodesText] = useState("");
  const [soldCount, setSoldCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // ===== State akun (tabel) =====
  // rows: array of { [fieldName]: string, ... }  -> tiap object = 1 akun
  const [rows, setRows] = useState([]);
  const [soldAccountCount, setSoldAccountCount] = useState(0);

  // Load data awal saat produk dipilih
  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!product?.id) return;
      setLoading(true);
      try {
        const { data } = await api.get(`/products/${product.id}/codes`);
        if (!active) return;
        const available = (data.available || []).map((c) => c.code).filter(Boolean);
        if (isVoucher) {
          setCodesText(available.join("\n"));
        } else {
          // Parse setiap baris kode: "field1|field2|field3" -> object per field
          const parsed = available.map((line) => {
            const parts = String(line).split("|");
            const row = {};
            akunFields.forEach((f, i) => { row[f] = (parts[i] || "").trim(); });
            return row;
          });
          setRows(parsed.length > 0 ? parsed : [emptyAkunRow(akunFields)]);
        }
        setSoldCount((data.sold || []).length);
        setSoldAccountCount((data.sold || []).length);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Gagal memuat kode");
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [product?.id, isVoucher]); // eslint-disable-line react-hooks/exhaustive-deps

  // ===== Voucher helpers =====
  const voucherParsedCount = isVoucher
    ? codesText.split(/\r?\n/).map((c) => c.trim()).filter((c) => c.length > 0).length
    : 0;

  // ===== Akun (tabel) helpers =====
  const emptyAkunRow = (fields) => {
    const r = {};
    fields.forEach((f) => { r[f] = ""; });
    return r;
  };
  const addRow = () => {
    if (rows.length >= 1000) {
      toast.error("Maksimal 1000 baris. Kurangi jumlah.");
      return;
    }
    setRows([...rows, emptyAkunRow(akunFields)]);
  };
  const removeRow = (idx) => {
    setRows(rows.filter((_, i) => i !== idx));
  };
  const updateCell = (idx, field, value) => {
    setRows(rows.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };
  const akunFilledCount = rows.filter((r) =>
    akunFields.some((f) => (r[f] || "").trim().length > 0)
  ).length;

  // ===== Save =====
  const save = async () => {
    if (!product?.id) return;

    let payload;
    if (isVoucher) {
      const list = codesText
        .split(/\r?\n/)
        .map((c) => c.trim())
        .filter((c) => c.length > 0);
      // dedupe case-insensitive
      const seen = new Set();
      const unique = [];
      for (const c of list) {
        const key = c.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        unique.push(c);
      }
      if (unique.length > 1000) {
        toast.error("Maksimal 1000 baris. Kurangi jumlah.");
        return;
      }
      payload = unique;
    } else {
      // Filter baris yang punya minimal 1 field terisi, lalu gabung dengan "|"
      // Urutan field dijaga = akunFields (sama dengan form Instant Delivery Fields).
      const list = rows
        .filter((r) => akunFields.some((f) => (r[f] || "").trim().length > 0))
        .map((r) => akunFields.map((f) => (r[f] || "").trim()).join("|"));
      // dedupe exact-match (kalau dua baris identik, simpan satu)
      const seen = new Set();
      const unique = [];
      for (const c of list) {
        if (seen.has(c)) continue;
        seen.add(c);
        unique.push(c);
      }
      if (unique.length > 1000) {
        toast.error("Maksimal 1000 baris. Kurangi jumlah.");
        return;
      }
      payload = unique;
    }

    setSaving(true);
    try {
      await api.put(`/products/${product.id}/codes`, { codes: payload });
      toast.success(
        isVoucher
          ? `Stok voucher tersimpan (${payload.length} kode tersedia)`
          : `Detail akun tersimpan (${payload.length} stok tersedia)`
      );
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-[#050505] border w-full max-w-3xl max-h-[90vh] overflow-y-auto"
        style={{ borderColor: `${accent}55` }}
        onClick={(e) => e.stopPropagation()}
        data-testid="manage-codes-modal"
      >
        <div className="p-6 border-b border-white/10 flex items-start justify-between">
          <div>
            <h3 className="font-display font-black text-2xl" style={{ color: accent }}>{title}</h3>
            <p className="text-xs text-white/50 mt-1">
              Edit stok {isVoucher ? "kode voucher" : "detail akun"} kapan saja. Perubahan langsung tersimpan & stok produk otomatis diperbarui.
            </p>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        {/* Pilih produk jika belum dipilih */}
        {!product ? (
          <div className="p-6">
            <label className="text-xs uppercase tracking-[0.2em] text-white/60">Pilih produk {kind}</label>
            {products.length === 0 ? (
              <p className="text-white/40 text-sm mt-3">Belum ada produk bertipe {kind}.</p>
            ) : (
              <div className="mt-3 space-y-2">
                {products.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => onPickProduct(p)}
                    className="w-full text-left border border-white/10 hover:border-white/30 p-3 flex items-center gap-3"
                    data-testid={`pick-product-${p.id}`}
                  >
                    <div className="w-12 h-12 bg-black border border-white/10 overflow-hidden shrink-0">
                      {p.image_url && <img src={resolveImg(p.image_url)} alt={p.title} className="w-full h-full object-cover" />}
                    </div>
                    <div className="min-w-0">
                      <p className="text-white text-sm truncate">{p.title}</p>
                      <p className="text-[10px] uppercase tracking-widest text-white/40">stok {p.stock}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="p-6 space-y-4">
            {/* Header produk */}
            <div className="flex items-center gap-3 border border-white/10 p-3">
              <div className="w-12 h-12 bg-black border border-white/10 overflow-hidden shrink-0">
                {product.image_url && <img src={resolveImg(product.image_url)} alt={product.title} className="w-full h-full object-cover" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-white text-sm truncate">{product.title}</p>
                <p className="text-[10px] uppercase tracking-widest text-white/40">
                  {isVoucher ? voucherParsedCount : akunFilledCount} tersedia · {isVoucher ? soldCount : soldAccountCount} terjual
                </p>
              </div>
              {products.length > 1 && (
                <button onClick={() => onPickProduct(null)} className="text-[10px] uppercase tracking-widest text-white/50 hover:text-white border border-white/15 px-2 py-1">
                  Ganti
                </button>
              )}
            </div>

            {/* === VOUCHER: textarea === */}
            {isVoucher ? (
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-xs uppercase tracking-[0.2em] text-white/60">
                    Daftar Kode Voucher
                  </label>
                  <span className="text-[10px] uppercase tracking-widest" style={{ color: accent }} data-testid="manage-codes-count">
                    {voucherParsedCount} baris · maks 1000
                  </span>
                </div>
                <textarea
                  rows={12}
                  value={codesText}
                  onChange={(e) => setCodesText(e.target.value)}
                  placeholder={"Satu kode voucher per baris, misal:\nABCD-1234-XYZ\nSTEAM-9999-0000"}
                  disabled={loading}
                  className="w-full bg-black border p-3 text-sm mt-2 font-mono disabled:opacity-50"
                  style={{ borderColor: `${accent}66` }}
                  data-testid="manage-codes-textarea"
                />
                <p className="text-[10px] text-white/40 mt-1">
                  Satu baris = satu kode. Kode yang SUDAH terjual tetap aman (tidak bisa dihapus dari sini). Stok = jumlah baris tersedia.
                </p>
              </div>
            ) : (
              /* === AKUN: TABEL per-field (konsisten dengan Instant Delivery Fields di form create) === */
              <div className="border border-[#00FF66]/30 bg-[#00FF66]/5 p-4">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-[#00FF66]" />
                    <span className="text-xs uppercase tracking-[0.2em] text-[#00FF66] font-bold">Instant Delivery Fields</span>
                  </div>
                  <span className="text-[10px] uppercase tracking-widest text-[#00FF66]" data-testid="manage-codes-count">
                    {akunFilledCount} baris · maks 1000
                  </span>
                </div>
                <p className="text-[10px] text-white/60 mb-3">
                  Isi field di bawah. Setiap baris = 1 akun yang akan diberikan ke buyer setelah pembayaran. Format & field sama dengan form pembuatan produk.
                </p>

                {/* Header kolom */}
                <div
                  className="grid gap-2 mb-2"
                  style={{ gridTemplateColumns: `30px 1fr 1fr 1fr 36px` }}
                >
                  <div className="text-[10px] uppercase tracking-widest text-white/40 text-center">#</div>
                  {akunFields.map((f) => (
                    <div key={f} className="text-[10px] uppercase tracking-widest text-white/70 capitalize">{f}</div>
                  ))}
                  <div></div>
                </div>

                {/* Baris-baris akun */}
                <div className="space-y-2 max-h-[400px] overflow-y-auto" data-testid="manage-akun-rows">
                  {rows.map((row, idx) => (
                    <div
                      key={idx}
                      className="grid gap-2 items-start"
                      style={{ gridTemplateColumns: `30px 1fr 1fr 1fr 36px` }}
                      data-testid={`manage-akun-row-${idx}`}
                    >
                      <div className="text-[10px] text-white/40 text-center pt-3">{idx + 1}</div>
                      {akunFields.map((f) => (
                        <input
                          key={f}
                          type="text"
                          value={row[f] || ""}
                          onChange={(e) => updateCell(idx, f, e.target.value)}
                          disabled={loading}
                          placeholder={`Masukkan ${f}...`}
                          className="w-full bg-black border border-[#00FF66]/40 focus:border-[#00FF66] p-2 text-sm disabled:opacity-50"
                          data-testid={`manage-akun-${idx}-${f}`}
                        />
                      ))}
                      <button
                        type="button"
                        onClick={() => removeRow(idx)}
                        disabled={rows.length <= 1}
                        className="text-white/50 hover:text-[#FF003C] p-1 disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Hapus baris"
                        data-testid={`manage-akun-remove-${idx}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={addRow}
                  disabled={loading}
                  className="mt-3 inline-flex items-center gap-2 px-3 py-2 border border-[#00FF66]/50 text-[#00FF66] hover:bg-[#00FF66]/10 text-[10px] uppercase tracking-widest font-bold disabled:opacity-50"
                  data-testid="manage-akun-add-row"
                >
                  <Plus className="w-3 h-3" /> Tambah Baris Akun
                </button>
                <p className="text-[10px] text-white/40 mt-2">
                  Akun yang SUDAH terjual tetap aman (tidak bisa dihapus dari sini). Stok = jumlah baris yang terisi.
                </p>
              </div>
            )}

            <div className="flex gap-2">
              <button
                onClick={save}
                disabled={saving || loading}
                className="flex-1 font-display font-black uppercase tracking-widest px-6 py-3 text-sm text-black disabled:opacity-50"
                style={{ background: accent }}
                data-testid="manage-codes-save"
              >
                {saving ? "Menyimpan..." : "Simpan Perubahan"}
              </button>
              <button onClick={onClose} className="px-4 py-3 border border-white/15 text-white/70 text-xs uppercase">Batal</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
